import React, { useState, useEffect, useCallback, useRef } from 'react'
import { supabase } from '../../lib/supabase'
import { useAuth } from '../../contexts/AuthContext'
import JobCard from './JobCard'
import JobDetailErrorBoundary from './JobDetailErrorBoundary'
import NewJobModal from './NewJobModal'
import GoogleCalendarSync from '../calendar/GoogleCalendarSync'
import { toISO as fmt } from '../../utils/formatters'
import { getBizColor, getStatusColor } from '../../utils/colors'
import { reorderByDate } from '../../utils/jobOrdering'
import { getTodayEastern } from '../../lib/dateHelpers'
import { resolveDueOnSite, saveDueOnSite } from '../../lib/dueOnSite'

const MONTHS = ['January','February','March','April','May','June','July','August','September','October','November','December']

function getWeekDates(date) {
  const d = new Date(date)
  const diff = d.getDate() - d.getDay()
  return Array.from({ length: 7 }, (_, i) => { const nd = new Date(d); nd.setDate(diff + i); return nd })
}

function getMonthDates(date) {
  const year = date.getFullYear()
  const month = date.getMonth()
  const firstDay = new Date(year, month, 1)
  const lastDay = new Date(year, month + 1, 0)
  const startPad = firstDay.getDay()
  const dates = []
  for (let i = 0; i < startPad; i++) { const d = new Date(firstDay); d.setDate(d.getDate() - (startPad - i)); dates.push({ date: d, currentMonth: false }) }
  for (let i = 1; i <= lastDay.getDate(); i++) { dates.push({ date: new Date(year, month, i), currentMonth: true }) }
  while (dates.length % 7 !== 0) { const last = dates[dates.length - 1].date; const nd = new Date(last); nd.setDate(nd.getDate() + 1); dates.push({ date: nd, currentMonth: false }) }
  return dates
}

function getEventColor(type) { return { follow_up: '#4a6a8c', bid_deadline: '#e05c5c', meeting: '#8c4a8c', service: '#b48c3c', rental_delivery: '#8c7a4a', reminder: '#6a8c4a' }[type] || '#b48c3c' }
function getWeatherIcon(code) { if (code === 0) return String.fromCodePoint(0x2600, 0xFE0F); if (code <= 2) return String.fromCodePoint(0x26C5); if (code <= 3) return String.fromCodePoint(0x2601, 0xFE0F); if (code <= 67) return String.fromCodePoint(0x1F327, 0xFE0F); if (code <= 77) return String.fromCodePoint(0x2744, 0xFE0F); return String.fromCodePoint(0x26C8, 0xFE0F) }

export default function CalendarPage() {
  const { profile, user, activeBusiness } = useAuth()
  const [view, setView] = useState(window.innerWidth < 768 ? 'day' : 'month')
  const [currentDate, setCurrentDate] = useState(new Date())
  const [jobs, setJobs] = useState([])
  const [linesByBid, setLinesByBid] = useState({})
  const [events, setEvents] = useState([])
  const [weather, setWeather] = useState(null)
  const [selectedJob, setSelectedJob] = useState(null)
  const [showNewJob, setShowNewJob] = useState(false)
  const [newJobDate, setNewJobDate] = useState(null)
  const [loading, setLoading] = useState(true)
  const [draggedJob, setDraggedJob] = useState(null)
  const [searchQuery, setSearchQuery] = useState('')
  // Rain Day — atomic flow: preview → confirm → shift+send+finalize → result.
  const [showRainDay, setShowRainDay] = useState(false)
  const [rainPhase, setRainPhase] = useState('preview') // 'preview' | 'running' | 'result'
  const [bumpDays, setBumpDays] = useState(1)
  const [notifyCustomers, setNotifyCustomers] = useState(true)
  const [rainDayDefault, setRainDayDefault] = useState(1)
  const [rainPreviewLoading, setRainPreviewLoading] = useState(false)
  const [rainPreview, setRainPreview] = useState(null) // { total, withEmail, withoutEmail, byOldDate: [{old, new, count}] }
  const [rainProgress, setRainProgress] = useState(null) // { stage, sent, failed, total, startedAt }
  const [rainResult, setRainResult] = useState(null) // { shifted, sent, failed, phone_calls, errors }
  // One-time reschedule-email queue (pending_reschedule_email = true on jobs).
  const [pendingResched, setPendingResched] = useState(null) // { queued, sample } | null
  const [showFireConfirm, setShowFireConfirm] = useState(false)
  const [fireRunning, setFireRunning] = useState(false)
  const [fireProgress, setFireProgress] = useState(null) // { sent, skipped, failed, errors, total }
  const [fireResult, setFireResult] = useState(null) // final summary after run
  const fetchRef = useRef(null)
  const touchStartX = useRef(null)

  const role = profile?.role
  const canEdit = role === 'owner' || role === 'office_staff' || role === 'project_manager' || role === 'crew_leader'
  const canSeeCollected = ['owner', 'office_staff', 'project_manager'].includes(role)
  const canEditDue = role === 'owner' || role === 'office_staff' || role === 'project_manager'
  const canViewDue = canEditDue || role === 'crew_leader'

  // Quick-edit popover for "Due on Site" — opened by long-press / right-click
  // on a job's $ amount in any calendar view.
  const [quickEdit, setQuickEdit] = useState(null) // { jobId, amount, anchorRect }
  const quickEditSave = async () => {
    if (!quickEdit) return
    const amt = quickEdit.amount === '' ? null : parseFloat(quickEdit.amount)
    if (quickEdit.amount !== '' && (isNaN(amt) || amt < 0)) { alert('Enter a valid dollar amount, or leave blank to clear.'); return }
    const { error } = await saveDueOnSite(quickEdit.jobId, { amount: amt, notes: quickEdit.notes || null, userId: profile?.id })
    if (error) { alert('Save failed: ' + error.message); return }
    setQuickEdit(null)
    fetchData(view, currentDate)
  }

  const weekDates = getWeekDates(currentDate)
  const monthDates = getMonthDates(currentDate)

  const fetchData = useCallback(async (vw, dt) => {
    setLoading(true)
    const activeView = vw || view
    const activeDate = dt || currentDate
    const wd = getWeekDates(activeDate)
    const md = getMonthDates(activeDate)

    let start, end
    if (activeView === 'day') { start = fmt(activeDate); end = fmt(activeDate) }
    else if (activeView === 'week') { start = fmt(wd[0]); end = fmt(wd[6]) }
    else { start = fmt(md[0].date); end = fmt(md[md.length - 1].date) }

    let jobQuery = supabase.from('jobs').select('*, profiles!jobs_crew_leader_id_fkey(full_name)').or(`start_date.lte.${end},end_date.gte.${start},end_date.is.null`).neq('status', 'cancelled').order('start_date', { ascending: false })
    if (activeBusiness !== 'all') jobQuery = jobQuery.eq('business_id', activeBusiness)
    if (role === 'laborer') jobQuery = jobQuery.contains('assigned_laborer_ids', [profile?.id])

    let eventQuery = supabase.from('calendar_events').select('*').gte('event_date', start).lte('event_date', end)
    if (activeBusiness !== 'all') eventQuery = eventQuery.eq('business_id', activeBusiness)

    const [{ data: jobData }, { data: eventData }] = await Promise.all([jobQuery, eventQuery])
    let jobs = jobData || []
    // Expand segmented jobs into per-segment virtual jobs so each visit renders
    // on its own date range. Clicks still open the parent job's detail view.
    const segmentedIds = jobs.filter(j => j.has_segments).map(j => j.id)
    if (segmentedIds.length > 0) {
      const { data: segData } = await supabase.from('job_segments').select('*').in('job_id', segmentedIds).order('segment_number', { ascending: true })
      const byJob = {}
      ;(segData || []).forEach(s => { (byJob[s.job_id] = byJob[s.job_id] || []).push(s) })
      const expanded = []
      jobs.forEach(j => {
        const segs = byJob[j.id]
        if (!j.has_segments || !segs || segs.length === 0) { expanded.push(j); return }
        segs.forEach(s => {
          expanded.push(Object.assign({}, j, {
            start_date: s.start_date,
            end_date: s.end_date || s.start_date,
            segment_number: s.segment_number,
            segment_count: segs.length,
            segment_description: s.description,
            segment_status: s.status,
            segment_id: s.id,
            title: (j.title || j.client_name || '') + ' — VISIT ' + s.segment_number + '/' + segs.length,
          }))
        })
      })
      jobs = expanded
    }
    setJobs(jobs)
    setEvents(eventData || [])
    setLoading(false)

    // Batch-load a tiny preview of bid_lines for every visible job with a bid.
    // One grouped query keeps the calendar fetch cheap — just description +
    // sort_order, nothing else.
    var bidIds = Array.from(new Set(jobs.map(function(j) { return j.bid_id }).filter(Boolean)))
    if (bidIds.length > 0) {
      supabase.from('bid_lines').select('bid_id, description, sort_order')
        .in('bid_id', bidIds).order('sort_order', { ascending: true })
        .then(function(res) {
          if (res.error) { console.warn('[CalendarPage] bid_lines preview failed', res.error); return }
          var byBid = {}
          ;(res.data || []).forEach(function(l) {
            if (!byBid[l.bid_id]) byBid[l.bid_id] = []
            byBid[l.bid_id].push(l.description)
          })
          setLinesByBid(byBid)
        })
    } else {
      setLinesByBid({})
    }
  }, [activeBusiness, role, profile, view, currentDate])

  useEffect(() => {
    if (fetchRef.current) clearTimeout(fetchRef.current)
    fetchRef.current = setTimeout(() => fetchData(view, currentDate), 100)
  }, [view, currentDate, activeBusiness, fetchData])

  useEffect(() => {
    fetch('https://api.open-meteo.com/v1/forecast?latitude=42.7&longitude=-83.7&daily=temperature_2m_max,temperature_2m_min,precipitation_probability_max,weathercode&temperature_unit=fahrenheit&timezone=America%2FDetroit&forecast_days=7')
      .then(r => r.json()).then(d => setWeather(d.daily)).catch(() => {})
  }, [])

  const [driveTimes, setDriveTimes] = useState({})
  const fetchDriveTimes = async (dayJobs) => {
    var withAddr = dayJobs.filter(j => j.site_address)
    if (withAddr.length < 2) { setDriveTimes({}); return }
    var addrs = withAddr.map(j => (j.site_address || '') + (j.site_city ? ', ' + j.site_city : '') + (j.site_state ? ', ' + j.site_state : ''))
    try {
      var resp = await fetch('/api/drive-times', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ addresses: addrs }) })
      var data = await resp.json()
      if (data.success && data.drive_times) {
        var map = {}
        data.drive_times.forEach(function(dt, i) { map[withAddr[i].id + '_' + withAddr[i+1].id] = dt })
        setDriveTimes(map)
      }
    } catch (e) { console.error('Drive times error:', e) }
  }
  // Push a date forward by N work days. Sundays don't count (crew works Mon-Sat).
  var addWorkDays = function(dateStr, days) {
    var d = new Date(dateStr + 'T12:00:00')
    var added = 0
    while (added < days) {
      d.setDate(d.getDate() + 1)
      if (d.getDay() !== 0) added++
    }
    return d.toISOString().split('T')[0]
  }

  // Load the configured default once on mount so the modal pre-fills with the
  // owner's preferred starting value. Falls back to 1 if the row is missing.
  useEffect(function() {
    supabase.from('app_settings').select('value').eq('key', 'RAIN_DAY_DEFAULT_BUMP_DAYS').maybeSingle().then(function(res) {
      var n = parseInt((res.data && res.data.value) || '1', 10)
      if (!isNaN(n) && n >= 1 && n <= 14) {
        setRainDayDefault(n)
        setBumpDays(n)
      }
    })
  }, [])

  // Atomic Rain Day target: every pool job with status scheduled/active and
  // start_date >= today. The preview shows the count grouped by current
  // start_date so the owner can see exactly which dates roll forward.
  var loadRainPreview = useCallback(async function(nDays) {
    setRainPreviewLoading(true)
    var today = getTodayEastern()
    var q = supabase.from('jobs')
      .select('id, client_name, client_email, site_address, start_date')
      .eq('business_id', 'pools')
      .in('status', ['scheduled', 'active'])
      .gte('start_date', today)
      .order('start_date', { ascending: true })
    var { data } = await q
    var rows = data || []
    var byOldDate = {}
    var withEmail = 0
    rows.forEach(function(r) {
      var k = r.start_date
      if (!byOldDate[k]) byOldDate[k] = { old: k, new: addWorkDays(k, nDays), count: 0 }
      byOldDate[k].count++
      if (r.client_email) withEmail++
    })
    var groups = Object.keys(byOldDate).sort().map(function(k) { return byOldDate[k] })
    setRainPreview({
      total: rows.length,
      withEmail: withEmail,
      withoutEmail: rows.length - withEmail,
      byOldDate: groups,
    })
    setRainPreviewLoading(false)
  }, [])

  var openRainDay = function() {
    setBumpDays(rainDayDefault)
    setNotifyCustomers(true)
    setRainPhase('preview')
    setRainPreview(null)
    setRainProgress(null)
    setRainResult(null)
    setShowRainDay(true)
    loadRainPreview(rainDayDefault)
  }

  // Re-pull the preview when the stepper changes (only while modal is open AND
  // we're still in preview phase — running/result phases hold their snapshot).
  useEffect(function() {
    if (showRainDay && rainPhase === 'preview') loadRainPreview(bumpDays)
  }, [bumpDays, showRainDay, rainPhase, loadRainPreview])

  // The atomic flow: stage=shift moves every affected job in one server call,
  // then we loop tiny stage=send batches (3 jobs × 2s throttle ≈ 7s each) so
  // the function stays under default Vercel timeouts and we get real progress.
  var executeRainDay = async function() {
    if (!rainPreview) return
    setRainPhase('running')
    setRainProgress({ stage: 'shifting', sent: 0, failed: 0, total: rainPreview.withEmail, startedAt: Date.now() })

    var shiftResp
    try {
      shiftResp = await fetch('/api/admin/apply-rain-day?stage=shift', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ bumpDays: bumpDays }),
      })
    } catch (err) {
      alert('Rain day shift failed: ' + err.message)
      setRainPhase('preview')
      return
    }
    var shiftData
    try { shiftData = await shiftResp.json() } catch (e) { shiftData = null }
    if (!shiftResp.ok || !shiftData) {
      alert('Rain day shift failed: ' + ((shiftData && shiftData.error) || shiftResp.statusText))
      setRainPhase('preview')
      return
    }

    var shifted = shiftData.shifted || 0
    var jobsToEmail = notifyCustomers ? (shiftData.jobs_to_email || []) : []
    var phoneCalls = shiftData.phone_calls || []
    var sent = 0, failed = 0
    var errors = []
    var total = jobsToEmail.length
    setRainProgress({ stage: 'sending', sent: 0, failed: 0, total: total, startedAt: Date.now() })

    // Batch of 3 keeps each send call well under the Vercel default timeout
    // (3 × 2.5s ≈ 7.5s) and gives a smooth-feeling progress update.
    var BATCH = 3
    for (var i = 0; i < jobsToEmail.length; i += BATCH) {
      var batch = jobsToEmail.slice(i, i + BATCH)
      try {
        var sendResp = await fetch('/api/admin/apply-rain-day?stage=send', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ bumpDays: bumpDays, jobs: batch }),
        })
        var data = await sendResp.json()
        if (sendResp.ok) {
          sent += data.sent || 0
          failed += data.failed || 0
          if (data.errors && data.errors.length) errors = errors.concat(data.errors)
        } else {
          failed += batch.length
          errors.push({ job_number: '(batch)', error: (data && data.error) || sendResp.statusText })
        }
      } catch (err) {
        failed += batch.length
        errors.push({ job_number: '(batch)', error: err.message })
      }
      setRainProgress({ stage: 'sending', sent: sent, failed: failed, total: total, startedAt: Date.now() })
    }

    // Finalize: log notification + record LAST_RAIN_DAY_APPLIED_AT.
    try {
      await fetch('/api/admin/apply-rain-day?stage=finalize', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          bumpDays: bumpDays,
          shifted: shifted,
          sent: sent,
          failed: failed,
          skipped: phoneCalls.length,
          userId: profile?.id || null,
        }),
      })
    } catch (err) { console.error('Rain day finalize failed', err) } // eslint-disable-line no-unused-vars

    setRainResult({ shifted: shifted, sent: sent, failed: failed, phone_calls: phoneCalls, errors: errors, notify: notifyCustomers })
    setRainPhase('result')
    fetchData(view, currentDate)
  }

  var isOwner = role === 'owner'

  // === Legacy reschedule-email queue runner ===
  // Kept around because pending_reschedule_email may still be set on jobs from
  // upstream rebalances. The atomic Rain Day flow above doesn't touch the flag,
  // but if it's non-zero from elsewhere this lets the owner drain it.

  var loadResched = useCallback(async function() {
    try {
      var resp = await fetch('/api/admin/fire-reschedule-emails', { method: 'GET' })
      if (!resp.ok) { setPendingResched({ queued: 0, sample: [] }); return }
      var data = await resp.json()
      setPendingResched({ queued: data.queued || 0, sample: data.sample || [] })
    } catch (err) { console.warn('reschedule queue fetch failed', err) } // eslint-disable-line no-unused-vars
  }, [])

  useEffect(function() { if (isOwner) loadResched() }, [isOwner, loadResched])

  var fireTestEmail = async function() {
    if (!user?.email) { alert('No login email on file — cannot send a test to yourself.'); return }
    setFireRunning(true)
    setFireResult(null)
    try {
      var resp = await fetch('/api/admin/fire-reschedule-emails?test=true&test_to=' + encodeURIComponent(user.email), { method: 'POST' })
      var data = await resp.json()
      if (!resp.ok) { alert('Test failed: ' + (data.error || resp.statusText)); }
      else { setFireResult({ test: true, sent: data.sent || 0, failed: data.failed || 0, skipped: data.skipped || 0, errors: data.errors || [], to: user.email }) }
    } catch (err) { alert('Test failed: ' + err.message) }
    setFireRunning(false)
  }

  var fireAllResched = async function() {
    setShowFireConfirm(false)
    if (!pendingResched || pendingResched.queued === 0) return
    var total = pendingResched.queued
    var totals = { sent: 0, skipped: 0, failed: 0, errors: [], total: total }
    setFireProgress(totals)
    setFireResult(null)
    setFireRunning(true)
    var safetyMaxBatches = Math.ceil(total / 5) + 5
    for (var i = 0; i < safetyMaxBatches; i++) {
      try {
        var resp = await fetch('/api/admin/fire-reschedule-emails?limit=5', { method: 'POST' })
        var data = await resp.json()
        if (!resp.ok) {
          totals.failed += 1
          totals.errors.push({ job_number: '(batch)', error: data.error || resp.statusText })
          setFireProgress(Object.assign({}, totals))
          break
        }
        totals.sent += data.sent || 0
        totals.skipped += data.skipped || 0
        totals.failed += data.failed || 0
        totals.errors = totals.errors.concat(data.errors || [])
        setFireProgress(Object.assign({}, totals))
        if ((data.remaining || 0) === 0) break
      } catch (err) {
        totals.failed += 1
        totals.errors.push({ job_number: '(batch)', error: err.message })
        setFireProgress(Object.assign({}, totals))
        break
      }
    }
    setFireResult({
      sent: totals.sent,
      skipped: totals.skipped,
      failed: totals.failed,
      errors: totals.errors,
      total: total,
    })
    setFireProgress(null)
    setFireRunning(false)
    loadResched()
    fetchData(view, currentDate)
  }

  var searchedJobs = jobs.filter(function(j) { if (!searchQuery) return true; var t = searchQuery.toLowerCase(); return [j.title, j.client_name, j.site_address, j.client_phone, j.client_email, j.job_number, j.status, j.start_date].some(function(f) { return f && String(f).toLowerCase().includes(t) }) })
  const getJobsForDay = (date) => { const s = fmt(date); return searchedJobs.filter(j => j.start_date <= s && (j.end_date || j.start_date) >= s) }
  const getEventsForDay = (date) => { const s = fmt(date); return events.filter(e => e.event_date === s) }
  const getWeatherForDay = (date) => {
    if (!weather) return null
    const s = fmt(date)
    const idx = weather.time?.indexOf(s)
    if (idx < 0) return null
    return { max: Math.round(weather.temperature_2m_max[idx]), min: Math.round(weather.temperature_2m_min[idx]), rain: weather.precipitation_probability_max[idx], code: weather.weathercode[idx] }
  }

  const isToday = (date) => fmt(date) === fmt(new Date())

  const handleTouchStart = (e) => { touchStartX.current = e.touches[0].clientX }
  const handleTouchEnd = (e) => {
    if (touchStartX.current === null) return
    const diff = touchStartX.current - e.changedTouches[0].clientX
    if (Math.abs(diff) > 50) { navigate(diff > 0 ? 1 : -1) }
    touchStartX.current = null
  }

  const navigate = (dir) => {
    const d = new Date(currentDate)
    if (view === 'day') d.setDate(d.getDate() + dir)
    else if (view === 'week') d.setDate(d.getDate() + dir * 7)
    else d.setMonth(d.getMonth() + dir)
    setCurrentDate(d)
  }

  const handleDrop = async (date, jobId) => {
    if (!canEdit) return
    const newDate = fmt(date)
    await supabase.from('jobs').update({ start_date: newDate }).eq('id', jobId)
    const moved = jobs.find(j => j.id === jobId)
    if (moved && moved.status !== 'completed' && moved.status !== 'cancelled') {
      try { await reorderByDate(jobId, newDate, moved.business_id) } catch (err) { console.error(err) }
    }
    setDraggedJob(null)
    fetchData(view, currentDate)
  }

  const openNewJob = (date) => {
    setNewJobDate(fmt(date))
    setShowNewJob(true)
  }

  const handleMonthCellClick = (date, dayJobs) => {
    if (dayJobs.length === 0 && canEdit) {
      openNewJob(date)
    } else {
      setCurrentDate(date)
      setView('day')
    }
  }

  const getTitle = () => {
    if (view === 'day') return ['SUN','MON','TUE','WED','THU','FRI','SAT'][currentDate.getDay()] + ', ' + MONTHS[currentDate.getMonth()] + ' ' + currentDate.getDate() + ', ' + currentDate.getFullYear()
    if (view === 'week') return MONTHS[weekDates[0].getMonth()] + ' ' + weekDates[0].getFullYear()
    return MONTHS[currentDate.getMonth()] + ' ' + currentDate.getFullYear()
  }

  return (
    <div style={styles.container}>
      {showRainDay && (() => {
        var running = rainPhase === 'running'
        var dateLabel = function(iso) {
          if (!iso) return ''
          var todayKey = getTodayEastern()
          var dt = new Date(iso + 'T12:00:00')
          var pretty = dt.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' })
          return iso === todayKey ? 'Today (' + pretty + ')' : pretty
        }
        var formatPhone = function(p) { return p || 'no phone on file' }
        var elapsed = rainProgress ? Math.max(0, Math.round((Date.now() - rainProgress.startedAt) / 1000)) : 0 // eslint-disable-line no-unused-vars
        var pct = (rainProgress && rainProgress.total > 0) ? Math.round((rainProgress.sent + rainProgress.failed) / rainProgress.total * 100) : 0
        return <div style={{position:'fixed',inset:0,backgroundColor:'rgba(0,0,0,0.75)',display:'flex',alignItems:'center',justifyContent:'center',zIndex:1000,padding:'24px'}} onClick={function(){ if (!running) setShowRainDay(false) }}>
          <div style={{backgroundColor:'#0f1420',border:'1px solid rgba(255,255,255,0.1)',borderRadius:'6px',padding:'28px',width:'100%',maxWidth:'560px',maxHeight:'85vh',overflow:'auto'}} onClick={function(e){e.stopPropagation()}}>
            <div style={{display:'flex',justifyContent:'space-between',alignItems:'center',marginBottom:'8px'}}>
              <div style={{fontFamily:"'Barlow Condensed',sans-serif",fontSize:'18px',fontWeight:700,letterSpacing:'2px',color: rainPhase === 'result' ? '#4a8c6a' : '#4a6a8c'}}>
                {rainPhase === 'result' ? String.fromCodePoint(0x2713) + ' RAIN DAY APPLIED' : 'RAIN DAY'}
              </div>
              <button disabled={running} onClick={function(){ setShowRainDay(false) }} style={{background:'none',border:'none',color:'rgba(255,255,255,0.4)',fontSize:'18px',cursor: running ? 'not-allowed' : 'pointer'}}>X</button>
            </div>

            {rainPhase === 'preview' && <>
              <div style={{fontSize:'12px',color:'rgba(255,255,255,0.35)',marginBottom:'18px'}}>Push every scheduled pool job from today forward by N work days (Sundays don't count). Emails go out in the same click.</div>
              <div style={{marginBottom:'16px'}}>
                <div style={{fontFamily:"'Barlow Condensed',sans-serif",fontSize:'10px',fontWeight:600,letterSpacing:'2px',color:'rgba(255,255,255,0.4)',marginBottom:'8px'}}>PUSH EVERYONE BACK BY HOW MANY DAYS?</div>
                <div style={{display:'flex',alignItems:'center',gap:'8px'}}>
                  <button onClick={function(){setBumpDays(function(n){return Math.max(1, n - 1)})}} disabled={bumpDays <= 1} style={{width:'40px',height:'40px',backgroundColor:'rgba(255,255,255,0.04)',border:'1px solid rgba(255,255,255,0.12)',borderRadius:'4px',color:'#ffffff',fontSize:'20px',fontWeight:700,cursor:'pointer',opacity:bumpDays <= 1 ? 0.3 : 1}}>{String.fromCodePoint(0x2212)}</button>
                  <input type="number" min="1" max="14" value={bumpDays} onChange={function(e){var v = parseInt(e.target.value || '1', 10); if (isNaN(v)) v = 1; setBumpDays(Math.max(1, Math.min(14, v)))}} style={{width:'80px',height:'40px',textAlign:'center',backgroundColor:'rgba(255,255,255,0.04)',border:'1px solid rgba(74,106,140,0.4)',borderRadius:'4px',color:'#ffffff',fontSize:'18px',fontFamily:"'Barlow Condensed',sans-serif",fontWeight:700,outline:'none'}} />
                  <button onClick={function(){setBumpDays(function(n){return Math.min(14, n + 1)})}} disabled={bumpDays >= 14} style={{width:'40px',height:'40px',backgroundColor:'rgba(255,255,255,0.04)',border:'1px solid rgba(255,255,255,0.12)',borderRadius:'4px',color:'#ffffff',fontSize:'20px',fontWeight:700,cursor:'pointer',opacity:bumpDays >= 14 ? 0.3 : 1}}>+</button>
                  <div style={{fontSize:'12px',color:'rgba(255,255,255,0.35)',marginLeft:'4px'}}>work day{bumpDays === 1 ? '' : 's'} (max 14)</div>
                </div>
              </div>
              <label style={{display:'flex',alignItems:'center',gap:'8px',marginBottom:'16px',cursor:'pointer',userSelect:'none'}}>
                <input type="checkbox" checked={notifyCustomers} onChange={function(e){setNotifyCustomers(e.target.checked)}} style={{width:'16px',height:'16px',cursor:'pointer'}} />
                <span style={{fontSize:'13px',color:'rgba(255,255,255,0.7)'}}>Email each customer with their new date</span>
              </label>
              <div style={{fontFamily:"'Barlow Condensed',sans-serif",fontSize:'10px',fontWeight:600,letterSpacing:'2px',color:'rgba(255,255,255,0.3)',marginBottom:'8px'}}>AFFECTED JOBS (TODAY + EVERYTHING AFTER){rainPreviewLoading ? ' — LOADING...' : ''}</div>
              <div style={{maxHeight:'240px',overflow:'auto',marginBottom:'12px',border:'1px solid rgba(255,255,255,0.05)',borderRadius:'3px'}}>
                {!rainPreviewLoading && rainPreview && rainPreview.byOldDate.length === 0 && <div style={{padding:'24px',textAlign:'center',color:'rgba(255,255,255,0.25)',fontSize:'13px'}}>No scheduled pool jobs from today forward.</div>}
                {rainPreview && rainPreview.byOldDate.map(function(g) {
                  var newStr = new Date(g.new + 'T12:00:00').toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' })
                  return <div key={g.old} style={{padding:'8px 12px',backgroundColor:'rgba(255,255,255,0.02)',borderBottom:'1px solid rgba(255,255,255,0.04)',display:'flex',justifyContent:'space-between',alignItems:'center',gap:'8px',fontSize:'13px'}}>
                    <div style={{color:'rgba(255,255,255,0.85)'}}>{String.fromCodePoint(0x2022)} {dateLabel(g.old)}: <b>{g.count}</b> job{g.count === 1 ? '' : 's'} {String.fromCodePoint(0x2192)} <span style={{color:'#4a8c6a',fontWeight:600}}>{newStr}</span></div>
                  </div>
                })}
              </div>
              {rainPreview && <div style={{fontSize:'12px',color:'rgba(255,255,255,0.55)',marginBottom:'16px',lineHeight:1.55}}>
                <div><b>Total:</b> {rainPreview.total} job{rainPreview.total === 1 ? '' : 's'} shifted, {notifyCustomers ? rainPreview.withEmail : 0} email{(notifyCustomers ? rainPreview.withEmail : 0) === 1 ? '' : 's'} to send</div>
                <div>Phone calls needed: <b>{rainPreview.withoutEmail}</b> (no email on file)</div>
              </div>}
              <div style={{display:'flex',gap:'8px'}}>
                <button onClick={function(){ setShowRainDay(false) }} style={{padding:'12px 20px',backgroundColor:'transparent',border:'1px solid rgba(255,255,255,0.1)',borderRadius:'3px',color:'rgba(255,255,255,0.5)',fontFamily:"'Barlow Condensed',sans-serif",fontSize:'13px',fontWeight:600,letterSpacing:'1.5px',cursor:'pointer'}}>CANCEL</button>
                <button disabled={!rainPreview || rainPreview.total === 0} onClick={executeRainDay} style={{flex:1,padding:'12px',backgroundColor:'#4a6a8c',border:'none',borderRadius:'3px',color:'#ffffff',fontFamily:"'Barlow Condensed',sans-serif",fontSize:'13px',fontWeight:700,letterSpacing:'2px',cursor: (!rainPreview || rainPreview.total === 0) ? 'not-allowed' : 'pointer',opacity:(!rainPreview || rainPreview.total === 0) ? 0.5 : 1}}>APPLY RAIN DAY</button>
              </div>
            </>}

            {rainPhase === 'running' && <div>
              <div style={{fontSize:'13px',color:'rgba(255,255,255,0.7)',marginBottom:'14px'}}>
                {rainProgress && rainProgress.stage === 'shifting' && 'Shifting schedule... '}
                {rainProgress && rainProgress.stage === 'sending' && ('Sending emails... ' + rainProgress.sent + ' / ' + rainProgress.total)}
              </div>
              <div style={{height:'12px',backgroundColor:'rgba(255,255,255,0.06)',borderRadius:'6px',overflow:'hidden',marginBottom:'10px'}}>
                <div style={{height:'100%',width: pct + '%',backgroundColor:'#4a6a8c',transition:'width 0.3s ease'}}></div>
              </div>
              <div style={{fontSize:'11px',color:'rgba(255,255,255,0.35)',display:'flex',justifyContent:'space-between'}}>
                <span>{pct}%</span>
                {rainProgress && rainProgress.failed > 0 && <span style={{color:'#e05c5c'}}>{rainProgress.failed} failed</span>}
              </div>
              <div style={{fontSize:'11px',color:'rgba(255,255,255,0.3)',marginTop:'14px'}}>2-second pause between sends keeps us under Gmail's rate limit — please don't close this tab.</div>
            </div>}

            {rainPhase === 'result' && rainResult && <div>
              <div style={{fontSize:'13px',color:'rgba(255,255,255,0.85)',marginBottom:'14px',lineHeight:1.7}}>
                <div>{String.fromCodePoint(0x2022)} <b>{rainResult.shifted}</b> job{rainResult.shifted === 1 ? '' : 's'} shifted +{bumpDays} day{bumpDays === 1 ? '' : 's'}</div>
                {rainResult.notify && <div>{String.fromCodePoint(0x2022)} <b>{rainResult.sent}</b> customer{rainResult.sent === 1 ? '' : 's'} emailed{rainResult.failed > 0 && <span style={{color:'#e05c5c'}}> ({rainResult.failed} failed — retry below)</span>}</div>}
                {!rainResult.notify && <div>{String.fromCodePoint(0x2022)} No emails sent (notify-customers was off)</div>}
                {rainResult.phone_calls.length > 0 && <div>{String.fromCodePoint(0x2022)} <b>{rainResult.phone_calls.length}</b> customer{rainResult.phone_calls.length === 1 ? '' : 's'} need a phone call:</div>}
              </div>
              {rainResult.phone_calls.length > 0 && <div style={{maxHeight:'220px',overflow:'auto',marginBottom:'14px',border:'1px solid rgba(224,92,92,0.25)',borderRadius:'3px',backgroundColor:'rgba(224,92,92,0.04)'}}>
                {rainResult.phone_calls.map(function(pc) {
                  var newStr = new Date(pc.new_start_date + 'T12:00:00').toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' })
                  var phoneRaw = pc.client_phone || ''
                  var telHref = phoneRaw ? 'tel:' + phoneRaw.replace(/[^\d+]/g, '') : null
                  return <div key={pc.id} style={{padding:'10px 14px',borderBottom:'1px solid rgba(255,255,255,0.05)',display:'flex',justifyContent:'space-between',alignItems:'center',gap:'8px',fontSize:'13px'}}>
                    <div style={{minWidth:0,flex:1}}>
                      <div style={{color:'#ffffff',fontWeight:600,overflow:'hidden',textOverflow:'ellipsis',whiteSpace:'nowrap'}}>{pc.client_name || 'Unnamed'}</div>
                      <div style={{fontSize:'11px',color:'rgba(255,255,255,0.4)'}}>{pc.job_number} {String.fromCodePoint(0x2192)} <span style={{color:'#4a8c6a'}}>{newStr}</span></div>
                    </div>
                    {telHref ? <a href={telHref} style={{color:'#b48c3c',fontSize:'13px',fontWeight:700,textDecoration:'none',whiteSpace:'nowrap'}}>{formatPhone(phoneRaw)}</a>
                              : <span style={{fontSize:'12px',color:'rgba(255,255,255,0.3)',whiteSpace:'nowrap'}}>{formatPhone(phoneRaw)}</span>}
                  </div>
                })}
              </div>}
              {rainResult.errors && rainResult.errors.length > 0 && <div style={{maxHeight:'120px',overflow:'auto',marginBottom:'12px',border:'1px solid rgba(224,92,92,0.3)',borderRadius:'3px',padding:'8px 10px',fontSize:'11px',color:'rgba(255,255,255,0.55)'}}>
                <div style={{fontFamily:"'Barlow Condensed',sans-serif",letterSpacing:'1.5px',color:'#e05c5c',marginBottom:'4px'}}>EMAIL FAILURES (call these customers):</div>
                {rainResult.errors.map(function(e, i) { return <div key={i}>{String.fromCodePoint(0x2022)} {e.job_number}: {e.error}</div> })}
              </div>}
              <button onClick={function(){ setShowRainDay(false) }} style={{width:'100%',padding:'12px',backgroundColor:'#4a8c6a',border:'none',borderRadius:'3px',color:'#0a0f1a',fontFamily:"'Barlow Condensed',sans-serif",fontSize:'13px',fontWeight:700,letterSpacing:'2px',cursor:'pointer'}}>DONE</button>
            </div>}
          </div>
        </div>
      })()}

      {showFireConfirm && pendingResched && (
        <div style={{position:'fixed',inset:0,backgroundColor:'rgba(0,0,0,0.75)',display:'flex',alignItems:'center',justifyContent:'center',zIndex:1100,padding:'24px'}} onClick={function(){setShowFireConfirm(false)}}>
          <div style={{backgroundColor:'#0f1420',border:'1px solid rgba(224,92,92,0.45)',borderRadius:'6px',padding:'24px',width:'100%',maxWidth:'480px'}} onClick={function(e){e.stopPropagation()}}>
            <div style={{fontFamily:"'Barlow Condensed',sans-serif",fontSize:'16px',fontWeight:700,letterSpacing:'2px',color:'#e05c5c',marginBottom:'12px'}}>{String.fromCodePoint(0x1F6A8)} FIRE RESCHEDULE EMAILS</div>
            <div style={{fontSize:'14px',color:'rgba(255,255,255,0.85)',marginBottom:'14px',lineHeight:1.5}}>
              Send <b>{pendingResched.queued}</b> rescheduling email{pendingResched.queued === 1 ? '' : 's'} now? This cannot be undone — emails go out immediately and the queue is cleared on each successful send.
            </div>
            <div style={{fontSize:'12px',color:'rgba(255,255,255,0.45)',marginBottom:'18px',lineHeight:1.5}}>
              Recommended: run a test send to your own inbox first to verify formatting.
            </div>
            <div style={{display:'flex',gap:'8px',flexWrap:'wrap'}}>
              <button onClick={function(){setShowFireConfirm(false)}} style={{padding:'10px 16px',backgroundColor:'transparent',border:'1px solid rgba(255,255,255,0.15)',borderRadius:'3px',color:'rgba(255,255,255,0.6)',fontFamily:"'Barlow Condensed',sans-serif",fontSize:'12px',fontWeight:600,letterSpacing:'1.5px',cursor:'pointer'}}>CANCEL</button>
              <button onClick={function(){ setShowFireConfirm(false); fireTestEmail() }} disabled={fireRunning || !user?.email} style={{padding:'10px 16px',backgroundColor:'rgba(180,140,60,0.18)',border:'1px solid rgba(180,140,60,0.45)',borderRadius:'3px',color:'#b48c3c',fontFamily:"'Barlow Condensed',sans-serif",fontSize:'12px',fontWeight:700,letterSpacing:'1.5px',cursor: (fireRunning || !user?.email) ? 'not-allowed' : 'pointer',opacity:(fireRunning || !user?.email) ? 0.5 : 1}}>SEND 1 TEST TO ME</button>
              <button onClick={fireAllResched} disabled={fireRunning} style={{flex:1,minWidth:'160px',padding:'10px 16px',backgroundColor:'#e05c5c',border:'none',borderRadius:'3px',color:'#ffffff',fontFamily:"'Barlow Condensed',sans-serif",fontSize:'12px',fontWeight:700,letterSpacing:'2px',cursor:fireRunning ? 'wait' : 'pointer',opacity:fireRunning ? 0.6 : 1}}>FIRE ALL {pendingResched.queued}</button>
            </div>
          </div>
        </div>
      )}

      {fireProgress && (
        <div style={{position:'fixed',bottom:'24px',left:'50%',transform:'translateX(-50%)',zIndex:1200,backgroundColor:'#0f1420',border:'1px solid rgba(224,92,92,0.4)',borderRadius:'6px',padding:'14px 18px',boxShadow:'0 8px 28px rgba(0,0,0,0.5)',display:'flex',alignItems:'center',gap:'12px',maxWidth:'90vw'}}>
          <div style={{fontSize:'13px',color:'#ffffff'}}>
            Sending... {fireProgress.sent + fireProgress.skipped + fireProgress.failed}/{fireProgress.total}
            {fireProgress.skipped > 0 && <span style={{color:'rgba(255,255,255,0.5)',marginLeft:'8px'}}>({fireProgress.skipped} no email)</span>}
            {fireProgress.failed > 0 && <span style={{color:'#e05c5c',marginLeft:'8px'}}>({fireProgress.failed} failed)</span>}
          </div>
        </div>
      )}

      {fireResult && (
        <div style={{position:'fixed',bottom:'24px',left:'50%',transform:'translateX(-50%)',zIndex:1200,backgroundColor:'#0f1420',border:'1px solid ' + (fireResult.failed > 0 ? 'rgba(224,92,92,0.5)' : 'rgba(74,140,106,0.5)'),borderRadius:'6px',padding:'14px 18px',boxShadow:'0 8px 28px rgba(0,0,0,0.5)',maxWidth:'90vw',width:'520px'}}>
          <div style={{display:'flex',alignItems:'flex-start',justifyContent:'space-between',gap:'12px'}}>
            <div style={{fontSize:'13px',color:'#ffffff',flex:1}}>
              {fireResult.test ? (
                <div><b>Test sent</b> — 1 email routed to <span style={{color:'#b48c3c'}}>{fireResult.to}</span>. Check the inbox, then "Fire All" to send the real batch.</div>
              ) : (
                <div><b>Done.</b> Sent {fireResult.sent} | Skipped {fireResult.skipped} | Failed {fireResult.failed}{fireResult.total ? ' of ' + fireResult.total : ''}.</div>
              )}
              {fireResult.errors && fireResult.errors.length > 0 && (
                <div style={{marginTop:'8px',fontSize:'11px',color:'rgba(255,255,255,0.55)',maxHeight:'140px',overflow:'auto',borderTop:'1px solid rgba(255,255,255,0.08)',paddingTop:'6px'}}>
                  <div style={{fontFamily:"'Barlow Condensed',sans-serif",letterSpacing:'1.5px',color:'rgba(224,92,92,0.85)',marginBottom:'4px'}}>UNSENT (call these customers):</div>
                  {fireResult.errors.map(function(e, i) { return <div key={i}>{String.fromCodePoint(0x2022)} {e.job_number}: {e.error}</div> })}
                </div>
              )}
            </div>
            <button onClick={function(){ setFireResult(null) }} style={{background:'none',border:'none',color:'rgba(255,255,255,0.4)',fontSize:'18px',cursor:'pointer'}}>X</button>
          </div>
        </div>
      )}

      {selectedJob && (
        <JobDetailErrorBoundary key={selectedJob.id} job={selectedJob} onClose={() => setSelectedJob(null)}>
          <JobCard job={selectedJob} onClose={() => setSelectedJob(null)} onUpdate={() => { fetchData(view, currentDate); setSelectedJob(null) }} />
        </JobDetailErrorBoundary>
      )}

      {quickEdit && (() => {
        // Anchor under the long-pressed badge if we have a rect, else center.
        var rect = quickEdit.anchorRect
        var top = rect ? Math.min(window.innerHeight - 220, Math.max(8, rect.bottom + 6)) : Math.max(60, window.innerHeight / 2 - 80)
        var left = rect ? Math.min(window.innerWidth - 280, Math.max(8, rect.left)) : Math.max(8, window.innerWidth / 2 - 140)
        return (
          <div onClick={() => setQuickEdit(null)} style={{ position: 'fixed', inset: 0, backgroundColor: 'rgba(0,0,0,0.45)', zIndex: 1100 }}>
            <div onClick={e => e.stopPropagation()} style={{ position: 'fixed', top: top + 'px', left: left + 'px', width: '270px', backgroundColor: '#0f1420', border: '1px solid rgba(180,140,60,0.4)', borderRadius: '6px', padding: '14px', boxShadow: '0 8px 28px rgba(0,0,0,0.5)' }}>
              <div style={{ fontFamily: "'Barlow Condensed',sans-serif", fontSize: '11px', fontWeight: 700, letterSpacing: '2px', color: '#b48c3c', marginBottom: '8px' }}>QUICK EDIT — DUE ON SITE</div>
              <input
                autoFocus
                type="number"
                step="0.01"
                min="0"
                value={quickEdit.amount}
                onChange={e => setQuickEdit({ ...quickEdit, amount: e.target.value })}
                onKeyDown={e => { if (e.key === 'Enter') quickEditSave(); if (e.key === 'Escape') setQuickEdit(null) }}
                placeholder="0.00"
                style={{ width: '100%', padding: '10px 12px', backgroundColor: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.1)', borderRadius: '3px', color: '#ffffff', fontSize: '15px', fontFamily: "'Barlow Condensed',sans-serif", fontWeight: 700, outline: 'none', boxSizing: 'border-box', marginBottom: '8px' }}
              />
              <div style={{ fontSize: '10px', color: 'rgba(255,255,255,0.4)', marginBottom: '8px' }}>Leave blank to fall back to bid total.</div>
              <div style={{ display: 'flex', gap: '6px' }}>
                <button onClick={() => setQuickEdit(null)} style={{ padding: '8px 12px', backgroundColor: 'transparent', border: '1px solid rgba(255,255,255,0.15)', borderRadius: '3px', color: 'rgba(255,255,255,0.55)', fontFamily: "'Barlow Condensed',sans-serif", fontSize: '11px', fontWeight: 700, letterSpacing: '1.5px', cursor: 'pointer' }}>CANCEL</button>
                <button onClick={quickEditSave} style={{ flex: 1, padding: '8px', backgroundColor: '#b48c3c', border: 'none', borderRadius: '3px', color: '#0a0f1a', fontFamily: "'Barlow Condensed',sans-serif", fontSize: '11px', fontWeight: 700, letterSpacing: '1.5px', cursor: 'pointer' }}>SAVE</button>
              </div>
            </div>
          </div>
        )
      })()}
      {showNewJob && <NewJobModal defaultDate={newJobDate || fmt(currentDate)} activeBusiness={activeBusiness !== 'all' ? activeBusiness : 'pools'} onClose={() => setShowNewJob(false)} onSave={() => { fetchData(view, currentDate); setShowNewJob(false) }} />}

      <div style={styles.header}>
        <div>
          <div style={styles.title}>CALENDAR</div>
          <div style={styles.subtitle}>{getTitle()}</div>
        </div>
        <div style={styles.controls}>
          <div style={styles.viewToggle}>
            {['day', 'week', 'month'].map(v => (
              <button key={v} onClick={() => setView(v)} style={{ ...styles.viewBtn, ...(view === v ? styles.viewBtnActive : {}) }}>
                {v.toUpperCase()}
              </button>
            ))}
          </div>
          <button onClick={() => setCurrentDate(new Date())} style={styles.todayBtn}>TODAY</button>
          <button onClick={() => navigate(-1)} style={styles.navBtn}>{String.fromCodePoint(0x2190)}</button>
          <button onClick={() => navigate(1)} style={styles.navBtn}>{String.fromCodePoint(0x2192)}</button>
          {canEdit && <button onClick={() => openNewJob(currentDate)} style={styles.addBtn}>+ NEW JOB</button>}
          {canEdit && <button onClick={openRainDay} style={{padding:'8px 16px',backgroundColor:'rgba(74,106,140,0.15)',border:'1px solid rgba(74,106,140,0.3)',borderRadius:'3px',color:'#4a6a8c',fontFamily:"'Barlow Condensed',sans-serif",fontSize:'12px',fontWeight:600,letterSpacing:'1.5px',cursor:'pointer'}}>RAIN DAY</button>}
          {isOwner && pendingResched && pendingResched.queued > 0 && (
            <button onClick={function(){ setShowFireConfirm(true) }} disabled={fireRunning} style={{padding:'8px 16px',backgroundColor:'rgba(224,92,92,0.18)',border:'1px solid rgba(224,92,92,0.45)',borderRadius:'3px',color:'#e05c5c',fontFamily:"'Barlow Condensed',sans-serif",fontSize:'12px',fontWeight:700,letterSpacing:'1.5px',cursor: fireRunning ? 'wait' : 'pointer',opacity: fireRunning ? 0.6 : 1}}>{String.fromCodePoint(0x1F6A8)} FIRE {pendingResched.queued} RESCHEDULE EMAIL{pendingResched.queued === 1 ? '' : 'S'}</button>
          )}
        </div>
      </div>

      <div style={{marginBottom:'12px'}}><input value={searchQuery} onChange={function(e){setSearchQuery(e.target.value)}} style={{width:'100%',maxWidth:'500px',padding:'10px 16px',backgroundColor:'rgba(255,255,255,0.05)',border:'1px solid rgba(255,255,255,0.1)',borderRadius:'6px',color:'#ffffff',fontSize:'14px',fontFamily:"'Barlow',sans-serif",outline:'none',boxSizing:'border-box'}} placeholder="Search by name, address, phone, status..." /></div>
      <div onTouchStart={handleTouchStart} onTouchEnd={handleTouchEnd} style={{ flex: 1 }}>
        {loading ? <div style={styles.loading}>Loading...</div> : (
          <>
            {view === 'day' && (
              <div style={styles.dayView}>
                {getWeatherForDay(currentDate) && (() => { const w = getWeatherForDay(currentDate); return (
                  <div style={styles.weatherBar}>
                    <span style={{ fontSize: '24px' }}>{getWeatherIcon(w.code)}</span>
                    <span style={styles.weatherTemp}>{w.max}{String.fromCodePoint(0xB0)} / {w.min}{String.fromCodePoint(0xB0)}</span>
                    {w.rain > 0 && <span style={styles.weatherRain}>{String.fromCodePoint(0x1F4A7)} {w.rain}% chance of rain</span>}
                  </div>
                )})()}
                {getEventsForDay(currentDate).map(event => (
                  <div key={event.id} style={{ ...styles.dayEventBlock, borderLeft: '3px solid ' + getEventColor(event.event_type) }}>
                    <div style={{ color: getEventColor(event.event_type), fontFamily: "'Barlow Condensed',sans-serif", fontSize: '11px', letterSpacing: '1px' }}>{event.event_type?.replace('_', ' ').toUpperCase()}</div>
                    <div style={{ fontSize: '15px', color: '#fff', marginTop: '2px' }}>{event.title}</div>
                  </div>
                ))}
                {(() => { var dj = getJobsForDay(currentDate); return dj.map((job, idx) => <React.Fragment key={job.id + (job.segment_id ? ('-s' + job.segment_id) : '')}><JobBlock job={job} onClick={() => setSelectedJob(job)} getStatusColor={getStatusColor} getBizColor={getBizColor} expanded linePreview={job.bid_id ? linesByBid[job.bid_id] : null} canSeeCollected={canSeeCollected} canViewDue={canViewDue} canEditDue={canEditDue} onQuickEditDue={(j, rect) => setQuickEdit({ jobId: j.id, amount: j.due_on_site != null ? String(j.due_on_site) : String(resolveDueOnSite(j) || ''), notes: j.due_on_site_notes || '', anchorRect: rect })} />{(() => { var nextJob = dj[idx+1]; if (!nextJob) return null; var key = job.id + '_' + nextJob.id; var dt = driveTimes[key]; if (!dt || dt.error) return null; return <div style={{display:'flex',alignItems:'center',justifyContent:'center',gap:'8px',padding:'6px 0',color:'rgba(255,255,255,0.3)',fontSize:'11px'}}><div style={{flex:1,height:'1px',background:'rgba(255,255,255,0.06)'}}></div><span>{String.fromCodePoint(0x1F697)} {dt.duration_text} ({dt.distance_text})</span><div style={{flex:1,height:'1px',background:'rgba(255,255,255,0.06)'}}></div></div> })()}</React.Fragment>) })()}
                {getJobsForDay(currentDate).length === 0 && getEventsForDay(currentDate).length === 0 && <div style={styles.empty}>No jobs scheduled for this day.</div>}
                <div style={{display:'flex',gap:'8px',marginTop:'8px'}}>{canEdit && <button onClick={() => openNewJob(currentDate)} style={styles.addDayBtn}>+ Schedule a Job This Day</button>}{getJobsForDay(currentDate).filter(j => j.site_address).length >= 2 && <button onClick={() => fetchDriveTimes(getJobsForDay(currentDate))} style={{...styles.addDayBtn, background:'rgba(74,106,140,0.15)', borderColor:'rgba(74,106,140,0.3)', color:'#4a6a8c'}}>{String.fromCodePoint(0x1F697)} Drive Times</button>}</div>
              </div>
            )}

            {view === 'week' && (
              <div style={styles.weekGrid}>
                {weekDates.map((date, i) => {
                  const w = getWeatherForDay(date)
                  const dayJobs = getJobsForDay(date)
                  const dayEvents = getEventsForDay(date)
                  return (
                    <div key={i} style={{ ...styles.weekDayCol, ...(isToday(date) ? styles.weekDayColToday : {}) }} onDragOver={e => e.preventDefault()} onDrop={() => draggedJob && handleDrop(date, draggedJob)}>
                      <div style={{ ...styles.weekDayHeader, ...(isToday(date) ? styles.weekDayHeaderToday : {}) }} onClick={() => canEdit && openNewJob(date)}>
                        <div style={styles.weekDayName}>{['SUN','MON','TUE','WED','THU','FRI','SAT'][i]}</div>
                        <div style={{ ...styles.weekDayNum, ...(isToday(date) ? styles.weekDayNumToday : {}) }}>{date.getDate()}</div>
                        {w && <div style={styles.weekWeather}>{getWeatherIcon(w.code)} {w.max}{String.fromCodePoint(0xB0)}{w.rain > 30 && <span style={{ color: '#4a6a8c' }}> {String.fromCodePoint(0x1F4A7)}</span>}</div>}
                      </div>
                      <div style={styles.weekDayContent}>
                        {dayEvents.map(event => (
                          <div key={event.id} style={{ ...styles.weekEventBlock, borderLeft: '2px solid ' + getEventColor(event.event_type) }}>
                            <div style={{ fontSize: '10px', color: getEventColor(event.event_type) }}>{event.event_type?.replace('_', ' ')}</div>
                            <div style={{ fontSize: '12px', color: '#fff' }}>{event.title}</div>
                          </div>
                        ))}
                        {dayJobs.map(job => <JobBlock key={(job.id) + (job.segment_id ? ('-s' + job.segment_id) : '')} job={job} onClick={() => setSelectedJob(job)} getStatusColor={getStatusColor} getBizColor={getBizColor} draggable={canEdit && !job.segment_id} onDragStart={() => setDraggedJob(job.id)} linePreview={job.bid_id ? linesByBid[job.bid_id] : null} canSeeCollected={canSeeCollected} canViewDue={canViewDue} canEditDue={canEditDue} onQuickEditDue={(j, rect) => setQuickEdit({ jobId: j.id, amount: j.due_on_site != null ? String(j.due_on_site) : String(resolveDueOnSite(j) || ''), notes: j.due_on_site_notes || '', anchorRect: rect })} />)}
                        {canEdit && <button onClick={e => { e.stopPropagation(); openNewJob(date) }} style={styles.weekAddBtn}>+ add</button>}
                      </div>
                    </div>
                  )
                })}
              </div>
            )}

            {view === 'month' && (
              <div>
                <div style={styles.monthDayHeaders}>{['SUN','MON','TUE','WED','THU','FRI','SAT'].map(d => <div key={d} style={styles.monthDayHeaderCell}>{d}</div>)}</div>
                <div style={styles.monthGrid}>
                  {monthDates.map(({ date, currentMonth }, i) => {
                    const dayJobs = getJobsForDay(date)
                    return (
                      <div key={i} onClick={() => handleMonthCellClick(date, dayJobs)} style={{ ...styles.monthCell, ...(isToday(date) ? styles.monthCellToday : {}), ...(!currentMonth ? styles.monthCellOtherMonth : {}) }}>
                        <div style={{ ...styles.monthCellNum, ...(isToday(date) ? styles.monthCellNumToday : {}) }}>{date.getDate()}</div>
                        {dayJobs.slice(0, 3).map(job => {
                          var isComplete = job.status === 'completed'
                          return (
                          <div key={job.id + (job.segment_id ? ('-s' + job.segment_id) : '')} onClick={e => { e.stopPropagation(); setSelectedJob(job) }} style={{ ...styles.monthJobPill, backgroundColor: isComplete ? '#4a8c6a20' : job.status === 'cancelled' ? '#e05c5c20' : getBizColor(job.business_id) + '20', borderLeft: '2px solid ' + (isComplete ? '#4a8c6a' : job.status === 'cancelled' ? '#e05c5c' : getBizColor(job.business_id)) }}>
                            <span style={{ fontSize: '10px', color: '#fff', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', textDecoration: job.status === 'cancelled' ? 'line-through' : 'none' }}>
                              {isComplete && <span style={{ color: '#4a8c6a', marginRight: '3px' }}>{String.fromCodePoint(0x2713)}</span>}
                              {job.title || job.client_name}
                              {isComplete && canSeeCollected && job.amount_collected != null && <span style={{ color: '#4a8c6a', marginLeft: '4px', fontWeight: 700 }}>${Number(job.amount_collected).toLocaleString('en-US', { maximumFractionDigits: 0 })}</span>}
                            </span>
                          </div>
                          )
                        })}
                        {dayJobs.length > 3 && <div style={{ fontSize: '10px', color: 'rgba(255,255,255,0.35)', marginTop: '2px' }}>+{dayJobs.length - 3} more</div>}
                      </div>
                    )
                  })}
                </div>
              </div>
            )}
          </>
        )}

        {canEdit && <div style={{marginTop:'20px'}}><GoogleCalendarSync userId={profile?.id} /></div>}
        <div style={styles.legend}>
          {[{ color: '#4a6a8c', label: 'Scheduled' }, { color: '#b48c3c', label: 'Active / CP Pools' }, { color: '#e05c5c', label: 'On Hold' }, { color: '#4a8c6a', label: 'Completed / Riverbend' }].map(l => (
            <div key={l.label} style={styles.legendItem}><div style={{ ...styles.legendDot, backgroundColor: l.color }} /><span>{l.label}</span></div>
          ))}
        </div>
      </div>
    </div>
  )
}

function JobBlock({ job, onClick, getStatusColor, getBizColor, expanded, draggable, onDragStart, linePreview, canSeeCollected, canViewDue, canEditDue, onQuickEditDue }) {
  var previewLines = Array.isArray(linePreview) ? linePreview : []
  var topLines = previewLines.slice(0, expanded ? 4 : 2)
  var moreCount = previewLines.length - topLines.length
  // Tooltip lists every line so hover (desktop) shows the full scope. On mobile
  // the user taps the card to open the full detail view anyway.
  var tooltipText = previewLines.length > 0 ? ('Work scope:\n\u2022 ' + previewLines.join('\n\u2022 ')) : undefined
  var isComplete = job.status === 'completed'
  var collectedDisplay = isComplete && canSeeCollected && job.amount_collected != null
    ? '$' + Number(job.amount_collected).toLocaleString('en-US', { maximumFractionDigits: 0 })
    : null
  var due = canViewDue ? resolveDueOnSite(job) : 0
  var showDue = canViewDue && due > 0 && !isComplete && job.status !== 'cancelled'
  var isOverridden = job.due_on_site != null
  var dueRef = useRef(null)
  var pressTimer = useRef(null)
  var longPressed = useRef(false)
  var openQuickEdit = function(e) {
    if (!canEditDue || !onQuickEditDue) return
    if (e && e.preventDefault) e.preventDefault()
    if (e && e.stopPropagation) e.stopPropagation()
    var rect = dueRef.current ? dueRef.current.getBoundingClientRect() : null
    onQuickEditDue(job, rect)
  }
  var onDueTouchStart = function() {
    if (!canEditDue) return
    longPressed.current = false
    pressTimer.current = setTimeout(function() {
      longPressed.current = true
      if (navigator.vibrate) { try { navigator.vibrate(20) } catch (_) {} } // eslint-disable-line no-unused-vars
      openQuickEdit()
    }, 500)
  }
  var onDueTouchEnd = function() {
    if (pressTimer.current) clearTimeout(pressTimer.current)
  }
  var onDueClick = function(e) {
    // Eat the click after a long-press so it doesn't bubble into opening the
    // job card.
    if (longPressed.current) {
      e.stopPropagation()
      e.preventDefault()
      longPressed.current = false
    }
  }
  return (
    <div onClick={onClick} draggable={draggable} onDragStart={onDragStart} title={tooltipText} style={{ ...styles.jobBlock, borderLeft: '3px solid ' + (isComplete ? '#4a8c6a' : job.status === 'cancelled' ? '#e05c5c' : getBizColor(job.business_id)), backgroundColor: isComplete ? 'rgba(74,140,106,0.08)' : job.status === 'cancelled' ? 'rgba(224,92,92,0.08)' : undefined, opacity: isComplete ? 0.85 : 1, cursor: 'pointer' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
        <div style={styles.jobNumber}>{job.job_number}</div>
        <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
          {isComplete && <span style={{ fontSize: '12px', color: '#4a8c6a', fontWeight: 700, lineHeight: 1 }}>{String.fromCodePoint(0x2713)}</span>}
          <div style={{ width: '7px', height: '7px', borderRadius: '50%', backgroundColor: getStatusColor(job.status), flexShrink: 0, marginTop: '2px' }} />
        </div>
      </div>
      <div style={{ ...styles.jobClient, textDecoration: job.status === 'cancelled' ? 'line-through' : 'none' }}>{job.title || job.client_name}</div>
      {collectedDisplay && <div style={{ fontSize: '11px', color: '#4a8c6a', fontFamily: "'Barlow Condensed',sans-serif", fontWeight: 700, letterSpacing: '0.5px', marginTop: '2px' }}>{String.fromCodePoint(0x2713)} {collectedDisplay}</div>}
      {expanded && job.site_address && <div style={styles.jobDetail}>{String.fromCodePoint(0x1F4CD)} {job.site_address}</div>}
      {job.profiles?.full_name && <div style={styles.jobDetail}>{String.fromCodePoint(0x1F464)} {job.profiles.full_name}</div>}
      {expanded && job.equipment_assigned && <div style={styles.jobDetail}>{String.fromCodePoint(0x1F69C)} {job.equipment_assigned}</div>}
      {showDue && (
        <div
          ref={dueRef}
          onContextMenu={openQuickEdit}
          onTouchStart={onDueTouchStart}
          onTouchEnd={onDueTouchEnd}
          onTouchCancel={onDueTouchEnd}
          onClick={onDueClick}
          title={canEditDue ? 'Right-click or long-press to quick-edit' : undefined}
          style={{ marginTop: '4px', display: 'inline-flex', alignItems: 'center', gap: '4px', padding: '2px 6px', backgroundColor: isOverridden ? 'rgba(74,106,140,0.15)' : 'rgba(180,140,60,0.12)', border: '1px solid ' + (isOverridden ? 'rgba(74,106,140,0.35)' : 'rgba(180,140,60,0.3)'), borderRadius: '3px', fontFamily: "'Barlow Condensed',sans-serif", fontSize: '11px', fontWeight: 700, letterSpacing: '0.5px', color: isOverridden ? '#4a6a8c' : '#b48c3c', cursor: canEditDue ? 'context-menu' : 'default' }}
        >
          {String.fromCodePoint(0x1F4B0)} ${Number(due).toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 2 })}
        </div>
      )}
      {showDue && job.due_on_site_notes && (
        <div style={{ fontSize: '10px', color: 'rgba(255,255,255,0.4)', marginTop: '2px', fontStyle: 'italic', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{job.due_on_site_notes}</div>
      )}
      {topLines.length > 0 && (
        <div style={{ marginTop: '6px', paddingTop: '6px', borderTop: '1px dashed rgba(180,140,60,0.2)' }}>
          {topLines.map(function(desc, i) {
            return <div key={i} style={{ fontSize: '11px', color: 'rgba(255,255,255,0.55)', lineHeight: 1.35, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{String.fromCodePoint(0x2022)} {desc}</div>
          })}
          {moreCount > 0 && <div style={{ fontSize: '10px', color: 'rgba(180,140,60,0.7)', marginTop: '2px' }}>+{moreCount} more</div>}
        </div>
      )}
    </div>
  )
}

const styles = {
  container: { fontFamily: "'Barlow',sans-serif", maxWidth: '100%' },
  header: { display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', marginBottom: '24px', flexWrap: 'wrap', gap: '16px' },
  title: { fontFamily: "'Barlow Condensed',sans-serif", fontSize: '28px', fontWeight: 700, letterSpacing: '3px', color: '#ffffff' },
  subtitle: { fontSize: '15px', color: 'rgba(255,255,255,0.4)', marginTop: '4px' },
  controls: { display: 'flex', gap: '8px', alignItems: 'center', flexWrap: 'wrap' },
  viewToggle: { display: 'flex', gap: '2px' },
  viewBtn: { padding: '7px 12px', backgroundColor: 'transparent', border: '1px solid rgba(255,255,255,0.1)', color: 'rgba(255,255,255,0.4)', fontFamily: "'Barlow Condensed',sans-serif", fontSize: '11px', fontWeight: 600, letterSpacing: '1.5px', cursor: 'pointer', borderRadius: '3px' },
  viewBtnActive: { backgroundColor: 'rgba(180,140,60,0.15)', borderColor: 'rgba(180,140,60,0.4)', color: '#b48c3c' },
  todayBtn: { padding: '7px 14px', backgroundColor: 'transparent', border: '1px solid rgba(255,255,255,0.15)', borderRadius: '3px', color: 'rgba(255,255,255,0.6)', fontFamily: "'Barlow Condensed',sans-serif", fontSize: '12px', fontWeight: 600, letterSpacing: '1.5px', cursor: 'pointer' },
  navBtn: { padding: '7px 12px', backgroundColor: 'transparent', border: '1px solid rgba(255,255,255,0.1)', borderRadius: '3px', color: 'rgba(255,255,255,0.5)', fontSize: '16px', cursor: 'pointer' },
  addBtn: { backgroundColor: '#b48c3c', border: 'none', borderRadius: '3px', padding: '8px 16px', color: '#0a0f1a', fontFamily: "'Barlow Condensed',sans-serif", fontSize: '12px', fontWeight: 700, letterSpacing: '2px', cursor: 'pointer' },
  loading: { padding: '60px', textAlign: 'center', color: 'rgba(255,255,255,0.3)', fontSize: '15px' },
  empty: { padding: '40px', textAlign: 'center', color: 'rgba(255,255,255,0.2)', fontSize: '15px' },
  legend: { display: 'flex', gap: '20px', marginTop: '24px', flexWrap: 'wrap' },
  legendItem: { display: 'flex', alignItems: 'center', gap: '8px', fontSize: '12px', color: 'rgba(255,255,255,0.4)' },
  legendDot: { width: '10px', height: '10px', borderRadius: '50%' },
  dayView: { display: 'flex', flexDirection: 'column', gap: '12px' },
  weatherBar: { display: 'flex', alignItems: 'center', gap: '12px', padding: '12px 16px', backgroundColor: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.06)', borderRadius: '4px' },
  weatherTemp: { fontSize: '18px', color: '#ffffff', fontFamily: "'Barlow Condensed',sans-serif", fontWeight: 600 },
  weatherRain: { fontSize: '14px', color: '#4a6a8c' },
  dayEventBlock: { padding: '12px 16px', backgroundColor: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.06)', borderRadius: '4px' },
  addDayBtn: { marginTop: '8px', padding: '12px', backgroundColor: 'transparent', border: '1px dashed rgba(255,255,255,0.1)', borderRadius: '4px', color: 'rgba(255,255,255,0.3)', fontFamily: "'Barlow Condensed',sans-serif", fontSize: '13px', letterSpacing: '1px', cursor: 'pointer', width: '100%' },
  weekGrid: { display: 'grid', gridTemplateColumns: 'repeat(7,1fr)', gap: '4px', minHeight: '500px' },
  weekDayCol: { backgroundColor: 'rgba(255,255,255,0.02)', border: '1px solid rgba(255,255,255,0.05)', borderRadius: '4px', overflow: 'hidden', display: 'flex', flexDirection: 'column' },
  weekDayColToday: { border: '1px solid rgba(180,140,60,0.25)', backgroundColor: 'rgba(180,140,60,0.04)' },
  weekDayHeader: { padding: '10px 8px', borderBottom: '1px solid rgba(255,255,255,0.05)', textAlign: 'center', cursor: 'pointer' },
  weekDayHeaderToday: { backgroundColor: 'rgba(180,140,60,0.1)' },
  weekDayName: { fontFamily: "'Barlow Condensed',sans-serif", fontSize: '10px', letterSpacing: '1.5px', color: 'rgba(255,255,255,0.3)' },
  weekDayNum: { fontFamily: "'Barlow Condensed',sans-serif", fontSize: '20px', fontWeight: 700, color: 'rgba(255,255,255,0.6)', lineHeight: 1 },
  weekDayNumToday: { color: '#b48c3c' },
  weekWeather: { fontSize: '11px', color: 'rgba(255,255,255,0.35)', marginTop: '4px' },
  weekDayContent: { flex: 1, padding: '6px', display: 'flex', flexDirection: 'column', gap: '4px', cursor: 'pointer' },
  weekEventBlock: { padding: '4px 6px', backgroundColor: 'rgba(255,255,255,0.03)', borderRadius: '2px' },
  weekAddBtn: { marginTop: 'auto', padding: '4px', backgroundColor: 'transparent', border: '1px dashed rgba(255,255,255,0.08)', borderRadius: '3px', color: 'rgba(255,255,255,0.2)', fontFamily: "'Barlow Condensed',sans-serif", fontSize: '11px', cursor: 'pointer', width: '100%' },
  monthDayHeaders: { display: 'grid', gridTemplateColumns: 'repeat(7,1fr)', marginBottom: '4px' },
  monthDayHeaderCell: { textAlign: 'center', fontFamily: "'Barlow Condensed',sans-serif", fontSize: '11px', letterSpacing: '1.5px', color: 'rgba(255,255,255,0.25)', padding: '8px 0' },
  monthGrid: { display: 'grid', gridTemplateColumns: window.innerWidth < 768 ? 'repeat(7,1fr)' : 'repeat(7,1fr)', gap: '2px', fontSize: window.innerWidth < 768 ? '11px' : undefined },
  monthCell: { minHeight: '100px', padding: '6px', backgroundColor: 'rgba(255,255,255,0.02)', border: '1px solid rgba(255,255,255,0.04)', borderRadius: '3px', cursor: 'pointer', overflow: 'hidden' },
  monthCellToday: { border: '1px solid rgba(180,140,60,0.3)', backgroundColor: 'rgba(180,140,60,0.05)' },
  monthCellOtherMonth: { opacity: 0.35 },
  monthCellNum: { fontFamily: "'Barlow Condensed',sans-serif", fontSize: '14px', fontWeight: 600, color: 'rgba(255,255,255,0.5)', marginBottom: '4px' },
  monthCellNumToday: { color: '#b48c3c' },
  monthJobPill: { padding: '2px 4px', borderRadius: '2px', marginBottom: '2px', display: 'flex', overflow: 'hidden' },
  jobBlock: { padding: '8px 10px', backgroundColor: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.06)', borderRadius: '3px', cursor: 'pointer', marginBottom: '2px' },
  jobNumber: { fontFamily: "'Barlow Condensed',sans-serif", fontSize: '10px', letterSpacing: '1.5px', color: 'rgba(255,255,255,0.35)' },
  jobClient: { fontSize: '13px', fontWeight: 500, color: '#ffffff', marginTop: '2px' },
  jobDetail: { fontSize: '11px', color: 'rgba(255,255,255,0.35)', marginTop: '2px' },
}
